import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Abas,
  Aviso,
  Botao,
  CampoBusca,
  Carregando,
  Etiqueta,
  Icone,
  paginar,
  Paginacao,
  Selo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api, data, ErroApi, moeda, telefone } from '../../lib/api.ts';
import { exportarXlsx } from '../../lib/exportar.ts';
import type { LinhaInscricao, StatusInscricao } from '../../lib/tipos.ts';

const ABAS = [
  { valor: 'submitted' as const, rotulo: 'Enviadas' },
  { valor: 'draft' as const, rotulo: 'Rascunhos' },
  { valor: 'cancelled' as const, rotulo: 'Canceladas' },
];

export function Inscricoes() {
  const [aba, setAba] = useState<StatusInscricao>('submitted');
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<LinhaInscricao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);

  // Trocar de aba ou de busca volta para a primeira página.
  useEffect(() => setPagina(1), [aba, busca]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = new URLSearchParams({ status: aba });
      if (busca.trim()) params.set('busca', busca.trim());
      const { inscricoes } = await api<{ inscricoes: LinhaInscricao[] }>(
        `/api/admin/inscricoes?${params}`,
      );
      setLista(inscricoes);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar');
    } finally {
      setCarregando(false);
    }
  }, [aba, busca]);

  useEffect(() => {
    const t = setTimeout(() => void carregar(), 300);
    return () => clearTimeout(t);
  }, [carregar]);

  const visivel = paginar(lista, pagina);

  // A planilha leva a lista inteira, não só a página que está na tela.
  function exportar() {
    exportarXlsx(
      `inscricoes-${aba}`,
      lista.map((i) => ({
        Protocolo: i.protocolo,
        Adolescente: i.nomeAdolescente,
        Responsável: i.nomeResponsavel ?? '',
        Telefone: i.telefoneResponsavel ?? '',
        'E-mail': i.email,
        House: i.house ?? '',
        Valor: Number(i.valorTotal),
        'Tem saúde': i.temSaude ? 'sim' : 'não',
        Autorização: i.autorizacaoAssinadaEm ? data(i.autorizacaoAssinadaEm) : '',
        Enviada: i.enviadaEm ? data(i.enviadaEm) : '',
      })),
    );
  }

  return (
    <div className="pilha-6">
      <div className="linha linha-entre">
        <div className="pilha-3">
          <Etiqueta>Inscrições</Etiqueta>
          <h2>Fichas</h2>
        </div>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          disabled={lista.length === 0}
          onClick={exportar}
          icone={<Icone.Download tamanho={14} />}
        >
          Exportar planilha
        </Botao>
      </div>

      <div className="linha">
        <CampoBusca
          valor={busca}
          aoMudar={setBusca}
          placeholder="Buscar por adolescente, protocolo, e-mail ou telefone"
          rotuloAcessivel="Buscar fichas"
        />
        <Abas rotulo="Filtrar por situação" valor={aba} aoMudar={setAba} opcoes={ABAS} />
      </div>

      {aba === 'draft' && (
        <Aviso tipo="info">
          Rascunhos são fichas começadas e não enviadas. Vale avisar estes responsáveis alguns dias
          antes de fechar as inscrições.
        </Aviso>
      )}

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {carregando ? (
        <Carregando />
      ) : lista.length === 0 ? (
        <Vazio titulo="Nenhuma ficha nesta situação">
          {busca ? 'Tente outro termo de busca.' : undefined}
        </Vazio>
      ) : (
        <div className="tabela-caixa">
          <table className="tabela">
            <thead>
              <tr>
                <th>Protocolo</th>
                <th>Adolescente</th>
                <th>Responsável</th>
                <th>House</th>
                <th className="num">Valor</th>
                <th>Situação</th>
                <th>Enviada</th>
              </tr>
            </thead>
            <tbody>
              {visivel.itens.map((i) => (
                <tr key={i.id}>
                  <td className="mono">{i.protocolo}</td>
                  <td>
                    <Link to={`/painel/inscricoes/${i.id}`} style={{ fontWeight: 600 }}>
                      {i.nomeAdolescente}
                    </Link>
                  </td>
                  <td className="texto-fraco">
                    {i.nomeResponsavel ?? '—'}
                    <br />
                    {telefone(i.telefoneResponsavel)}
                  </td>
                  <td>{i.house ?? '—'}</td>
                  <td className="num mono">{moeda(i.valorTotal)}</td>
                  <td>
                    <span className="linha" style={{ gap: 'var(--gap-1)' }}>
                      {!i.temSaude && <Selo tom="alerta">sem saúde</Selo>}
                      {!i.autorizacaoAssinadaEm && <Selo tom="neutro">sem autorização</Selo>}
                      {i.temSaude && i.autorizacaoAssinadaEm && (
                        <Selo tom="sucesso" ponto>
                          em dia
                        </Selo>
                      )}
                    </span>
                  </td>
                  <td className="mono texto-fraco">{data(i.enviadaEm)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Paginacao total={lista.length} pagina={visivel.pagina} aoMudar={setPagina} />
    </div>
  );
}
